import {
  ApolloError,
  ApolloLink,
  Observable,
  type FetchResult,
  type NextLink,
  type Operation,
} from '@apollo/client/core';
import { isSubscriptionOperation } from '@apollo/client/utilities';
import debounce from 'debounce';

import { Analytics } from '@trello/atlassian-analytics';
import { sendErrorEvent } from '@trello/error-reporting';
import { dangerouslyGetFeatureGateSync } from '@trello/feature-gate-client';
import { safelyUpdateGraphqlWebsocketState } from '@trello/web-sockets';

import { cache } from '../cache';
import { sequenceNumberState } from '../sequenceNumberState';
import { queryFromPojo } from '../syncDeltaToCache/queryFromPojo';
import { extractObjectIdFromAri } from '../syncNativeToRest/getObjectIdFromCacheObject';
import type { JSONArray, JSONObject, JSONValue } from '../types';
import { getUniqueSubscriptionKey } from './utils/getUniqueSubscriptionKey';
import {
  getMappedSubscriptionTypename,
  shouldMapSubscriptionTypename,
} from './subscriptionTypenameMappings';

// NOTE: typeof [] === 'object'
const isObjectOrArray = (node: JSONValue): node is JSONArray | JSONObject =>
  typeof node === 'object';

const debouncedBroadcast = debounce(() => {
  // @ts-expect-error
  cache.broadcastWatches();
}, 100);

const getPathToNode = (parentKey: string, key: string): string => {
  const isRoot = parentKey === '';
  return isRoot ? key : `${parentKey}.${key}`;
};

const DELETION_TYPES = new Set<string>([
  'TrelloActionDeleted',
  'TrelloCardDeleted',
  'TrelloChecklistDeleted',
  'TrelloCustomFieldDeleted',
  'TrelloLabelDeleted',
  'TrelloPlannerCalendarDeleted',
  'TrelloPlannerCalendarEventCardDeleted',
  'TrelloProviderCalendarDeleted',
]);

const isDeletionType = (typename: string) => DELETION_TYPES.has(typename);

const deleteFromCache = (node: JSONObject) => {
  let objectId: string | undefined;
  let nonNativeType: string;

  // Special handling for actions, for two reasons:
  // 1. We use a single type 'TrelloActionDeleted' to represent all actions
  //    which means we don't have the typename of the action to delete
  //    which means we can't delete the native action
  // 2. We don't have ARIs for actions yet so using id?.split('/').pop()
  //    will not get us the object ID of the action
  if (node.__typename === 'TrelloActionDeleted') {
    if (typeof node.id === 'string') {
      objectId = node.id.includes('/')
        ? extractObjectIdFromAri(node.id)
        : node.id;
    }
    nonNativeType = 'Action';
  } else {
    node.__typename = getMappedSubscriptionTypename(node.__typename as string);
    const id = cache.identify(node);
    cache.evict({ id });

    const nativeToNonNativeTypeMap: Record<string, string> = {
      TrelloCard: 'Card',
      TrelloChecklist: 'Checklist',
      TrelloCustomField: 'CustomField',
      TrelloLabel: 'Label',
    };

    objectId = id?.split('/').pop();
    nonNativeType = nativeToNonNativeTypeMap[node.__typename];
  }

  // Cache syncing
  if (objectId && nonNativeType) {
    cache.evict({
      id: cache.identify({ id: objectId, __typename: nonNativeType }),
    });
  }
};

/**
 * Given an update object, go through the keys and execute
 * any side effects (mostly cache evictions). Return the keys corresponding
 * to the side effect events.
 */
const executeSideEffects = (object: JSONObject): string[] => {
  const sideEffectKeys = new Set<string>();

  Object.entries(object).forEach(([key, entry]) => {
    if (!Array.isArray(entry)) {
      return;
    }

    // Handle deletion events
    entry.forEach((item) => {
      if (item === null || !isObjectOrArray(item) || Array.isArray(item)) {
        return;
      }

      const typename = item.__typename;
      if (typeof typename !== 'string') {
        return;
      }

      if (isDeletionType(typename)) {
        deleteFromCache(item);
        sideEffectKeys.add(key);
      }
    });
  });

  return [...sideEffectKeys];
};

// Return the subscriptionName, operationName, and ids to track
// the number of channels we're creating. Manually select these
// variables so we don't accidentally send UGC in analytics.
export const getSubscriptionAnalyticsAttributes = (
  subscriptionName: string,
  mutableOperation: {
    operationName?: string;
    variables?: {
      nodeId?: string;
      nodeIds?: string[];
      memberId?: string;
      boardId?: string;
    };
  },
) => {
  const {
    operationName,
    variables: { nodeId, nodeIds = [], memberId, boardId } = {},
  } = mutableOperation;

  return {
    subscriptionName,
    ...(operationName && { operationName }),
    ...(nodeId && { nodeId }),
    ...(nodeIds.length > 0 && { nodeIds }),
    ...(memberId && { memberId }),
    ...(boardId && { boardId }),
  };
};

/**
 * Return a new object with valid values from the subscription response. Recurse through
 * the original response and use deltas to validate null values. Also, strip 'Updated'
 * from typenames to update normalized entities in the cache.
 *
 * See: https://hello.atlassian.net/wiki/spaces/TRFC/pages/4020701349
 *
 * @param {JSONObject | JSONArray} currentNode The current node to traverse in the subscription response
 * @param {string} parentKey The flattened path to the node in dot notation
 * @param {string[]} deltas The list of valid values in the array
 * @returns {JSONObject | JSONArray} A new object with cleaned subscription response
 */
export function cleanSubscriptionResponse(
  currentNode: JSONArray | JSONObject,
  parentKey: string,
  deltas: string[] | null,
): JSONArray | JSONObject {
  // Handle first function call if non-object is passed in
  // Otherwise, we only recurse through objects and arrays
  if (currentNode === null || !isObjectOrArray(currentNode)) {
    return currentNode;
  }

  // deltas is null on the initial subscription response, or "hydration."
  // On the initial response, don't skip any values.  Thereafter, skip values
  // if key is not included in deltas.
  const skipValue = (key: string) =>
    deltas === null ? false : !deltas.includes(key);

  // If array, iterate over elements
  if (Array.isArray(currentNode)) {
    const newArrayNode: JSONArray = [];
    currentNode.forEach((item, index) => {
      const newKey = getPathToNode(parentKey, String(index));
      if (item !== null && isObjectOrArray(item)) {
        // If item is object or array, recurse
        newArrayNode[index] = cleanSubscriptionResponse(item, newKey, deltas);
      } else if (item === null && skipValue(newKey)) {
        // If deltas is provided, Skip null values not contained therein
      } else {
        // Copy value to new node
        newArrayNode[index] = item;
      }
    });

    return newArrayNode;
  }

  // If object, iterate through keys
  const sideEffectKeys = executeSideEffects(currentNode);
  const keysToWrite = Object.keys(currentNode).filter(
    (key) => key !== '_deltas' && !sideEffectKeys.includes(key),
  );

  const newObjectNode: JSONObject = {};
  keysToWrite.forEach((key) => {
    const newKey = getPathToNode(parentKey, key);
    const item = currentNode[key];

    if (item !== null && isObjectOrArray(item)) {
      const cleanedData = cleanSubscriptionResponse(item, newKey, deltas);
      newObjectNode[key] = cleanedData;
    } else if (item === null && skipValue(newKey)) {
      // If deltas is provided, Skip null values not contained therein
    } else if (
      key === '__typename' &&
      typeof item === 'string' &&
      shouldMapSubscriptionTypename(item)
    ) {
      newObjectNode[key] = getMappedSubscriptionTypename(item);
    } else {
      // Copy value to new node
      newObjectNode[key] = item;
    }
  });

  return newObjectNode;
}

/**
 * These types aren't exported by @apollo/client/core, so we need to define them here.
 */
interface SubscriptionObserver<T> {
  closed: boolean;
  next(value: T): void;
  error(errorValue: unknown): void;
  complete(): void;
}

interface Subscription {
  closed: boolean;
  unsubscribe(): void;
}

/**
 * We want traceId to be mutable across functions, so wrap it in an object.
 * We can't pass a string or null directly across functions since it'll be
 * passed by value and not by reference.
 *
 * We also can't store this as a class property since it could get
 * overwritten by future subscriptions.
 */
interface TraceContext {
  traceId: string | null;
}

/**
 * Temporary solution to process subscription responses coming through native GraphQL,
 * while they have separate type and _deltas property on them.
 *
 * This is an Apollo link for ws messages the inspects the data and validates
 * null values based on their existence in _deltas. This handles server's
 * partial updates and writes them to the cache.
 * @example
 * const cacheSubscriptionResponseLink = new CacheSubscriptionResponseLink();
 * const client = new ApolloClient({
 *   cache: new InMemoryCache(),
 *   link: ApolloLink.from([cacheSubscriptionResponseLink, httpLink]),
 * });
 *
 * @param {Operation} operation The GraphQL operation being executed.
 * @param {NextLink} forward A function that forwards the operation to the next link in the chain.
 * @returns {Observable<FetchResult>} An observable that emits the result of the operation.
 */
export class CacheSubscriptionResponseLink extends ApolloLink {
  private readonly MAX_RETRY_COUNT = 1; // keep it low for now, don't want to overwhelm downstream services
  private readonly retryCountMap = new Map<string, number>(); // subscription name -> retry count
  constructor() {
    super();
  }

  request(
    operation: Operation,
    forward?: NextLink,
  ): Observable<FetchResult> | null {
    if (!forward) {
      return null;
    }
    if (!isSubscriptionOperation(operation.query)) {
      return forward(operation);
    }

    const operationCopy: Operation = JSON.parse(JSON.stringify(operation));

    // Process the operation and its result here
    return new Observable((observer) => {
      const unsubscribe = this.initializeSubscription(
        observer,
        operationCopy,
        forward,
      );
      // Cleanup
      return () => {
        unsubscribe();
      };
    });
  }

  initializeSubscription(
    observer: SubscriptionObserver<FetchResult>,
    operation: Operation,
    forward: NextLink,
  ) {
    const traceContext: TraceContext = {
      traceId: Analytics.startTask({
        taskName: 'create-subscription/socket/graphql',
        source: '@trello/graphql',
      }),
    };

    operation.extensions.trello = {
      traceId: traceContext.traceId,
    };

    const subscriptionKey = getUniqueSubscriptionKey(
      operation.operationName,
      operation.variables,
    );

    const subscription = forward(operation).subscribe({
      next: (result: FetchResult) => {
        // clear error count once it successfully connects
        this.retryCountMap.delete(subscriptionKey);
        this.subscriptionNext(result, operation, traceContext);
        observer.next(result);
      },
      error: async (error) => {
        await this.subscriptionError(
          error,
          traceContext,
          subscription,
          operation,
          observer,
          forward,
        );
      },
      complete: () => {
        if (traceContext.traceId) {
          Analytics.taskAborted({
            taskName: 'create-subscription/socket/graphql',
            source: '@trello/graphql',
            traceId: traceContext.traceId,
            attributes: { operationName: operation.operationName },
          });
          traceContext.traceId = null;
        }
        observer.complete();
      },
    });

    return () => {
      subscription.unsubscribe();
      if (traceContext.traceId) {
        Analytics.taskAborted({
          taskName: 'create-subscription/socket/graphql',
          source: '@trello/graphql',
          traceId: traceContext.traceId,
        });
        traceContext.traceId = null;
      }
    };
  }

  subscriptionNext(
    result: FetchResult,
    operation: Operation,
    traceContext: TraceContext,
  ) {
    if (result.data) {
      // There's only one root subscription at a time. Get the
      // subscriptionName, and then get its data.
      const subscriptionName = Object.keys(result.data.trello)[0];
      const operationName = operation.operationName;
      const variables = operation.variables;
      const isFeatureGateEnabled = dangerouslyGetFeatureGateSync(
        'goo_sequence_number_replays',
      );
      if (isFeatureGateEnabled) {
        const key = getUniqueSubscriptionKey(operationName, variables);
        const currentSequenceNumber = result.extensions?.sequenceNumber ?? null;
        if (currentSequenceNumber !== null) {
          sequenceNumberState.setValue((prev) => ({
            ...prev,
            [key]: currentSequenceNumber,
          }));
        }
      }

      if (traceContext.traceId) {
        Analytics.taskSucceeded({
          taskName: 'create-subscription/socket/graphql',
          source: '@trello/graphql',
          traceId: traceContext.traceId,
          attributes: getSubscriptionAnalyticsAttributes(
            subscriptionName,
            operation,
          ),
        });
        traceContext.traceId = null;
      }

      const data = result.data.trello[subscriptionName];

      const id = data.id;

      // The subscription response can't be trusted because it may have null fields that were
      // not included in server's partial update. Delete all null fields unless they were
      // explicitly included in _deltas
      const cleanedData = {
        trello: {
          [subscriptionName]: cleanSubscriptionResponse(data, '', data._deltas),
        },
      };

      try {
        /**
         * First try to write the query including the field arguments
         * if the feature gate is enabled. If something fails,
         * then we'll try again without the field arguments and
         * send an error to sentry which we can use to track
         * down the issue.
         *
         * We added this because the change is a bit risky and otherwise
         * we don't have great visibility into any errors.
         */
        cache.writeQuery({
          query: queryFromPojo(cleanedData, { id }, operation),
          data: cleanedData,
          variables: { id },
          broadcast: false, // don't broadcast every single update
        });
      } catch (error) {
        cache.writeQuery({
          query: queryFromPojo(cleanedData, { id }),
          data: cleanedData,
          variables: { id },
          broadcast: false,
        });
        sendErrorEvent({
          error: new Error(
            `Error writing query with field arguments. OperationName: ${operation.operationName}. ${error}`,
          ),
          metadata: {
            ownershipArea: 'trello-graphql-data',
            feature: 'cacheSubscriptionResponseLink',
          },
        });
      }

      if (
        result.extensions?.trello.traceId &&
        result.extensions.trello.spanId
      ) {
        Analytics.taskSucceeded({
          taskName: 'send-message',
          source: '@trello/graphql',
          traceId: result.extensions.trello.traceId,
          spanId: result.extensions.trello.spanId,
          attributes: { id, subscriptionName },
        });
      }

      // broadcast every 100ms
      debouncedBroadcast();
    }
  }

  async subscriptionError(
    error: unknown,
    traceContext: TraceContext,
    subscription: Subscription,
    operation: Operation,
    observer: SubscriptionObserver<FetchResult>,
    forward: NextLink,
  ) {
    const subscriptionKey = getUniqueSubscriptionKey(
      operation.operationName,
      operation.variables,
    );
    const currentErrorCount = this.retryCountMap.get(subscriptionKey) ?? 1;

    if (dangerouslyGetFeatureGateSync('goo_sequence_number_on_error')) {
      const sequenceNumber = this.sequenceNumberFromError(error);
      if (sequenceNumber !== null) {
        sequenceNumberState.setValue((prev) => ({
          ...prev,
          [subscriptionKey]: sequenceNumber,
        }));
      }
    }

    if (traceContext.traceId) {
      Analytics.taskFailed({
        taskName: 'create-subscription/socket/graphql',
        source: '@trello/graphql',
        traceId: traceContext.traceId,
        error,
        attributes: {
          operationName: operation.operationName,
          currentErrorCount,
        },
      });
      traceContext.traceId = null;
    }
    /**
     * If we encounter a retryable error then we can re-subscribe
     * so that the client can get subscription updates again.
     *
     * Don't call observer.error in this case because we want to
     * keep the observer alive, and calling the error method would
     * cleanup the observer.
     */

    if (
      dangerouslyGetFeatureGateSync('goo_resubscribe_on_error') &&
      this.shouldRetrySubscription(error) &&
      currentErrorCount <= this.MAX_RETRY_COUNT
    ) {
      await new Promise((resolve) =>
        setTimeout(resolve, Math.pow(2, currentErrorCount) * 1_000),
      );
      this.retryCountMap.set(subscriptionKey, currentErrorCount + 1);
      subscription.unsubscribe();
      this.initializeSubscription(observer, operation, forward);
    } else {
      sendErrorEvent({
        error,
        metadata: {
          ownershipArea: 'trello-graphql-data',
          feature: 'cacheSubscriptionResponseLink',
          errorCount: currentErrorCount,
        },
      });
      safelyUpdateGraphqlWebsocketState('disconnected');
      observer.error(error);
    }
  }

  private shouldRetrySubscription(error: unknown) {
    return (
      error instanceof ApolloError &&
      error.graphQLErrors.some(
        (err) =>
          err.extensions?.statusCode === 429 ||
          err.message.includes('429') ||
          err.message === "Replay in seconds can't be in the future",
      )
    );
  }

  private sequenceNumberFromError(error: unknown): number | null {
    if (!(error instanceof ApolloError)) {
      return null;
    }

    const sequenceErr = error.graphQLErrors.find((err) =>
      Number.isInteger(Number(err.extensions?.sequenceNumber)),
    );
    if (!sequenceErr?.extensions?.sequenceNumber) {
      return null;
    }
    const sequenceNumber = Number(sequenceErr.extensions.sequenceNumber);
    if (!Number.isInteger(sequenceNumber)) {
      return null;
    }
    return sequenceNumber;
  }
}

export const cacheSubscriptionResponseLink =
  new CacheSubscriptionResponseLink();
